'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useModal } from '@/lib/hooks/useModal';

import { type Dictionary } from '@/content/i18n/schema';
import { Section } from '@/components/ui/Section/Section';
import { Container } from '@/components/ui/Container/Container';
import CTAButton from '@/components/ui/buttons/CTAButton/CTAButton';
import BrandTitle from '@/components/ui/BrandTitle/BrandTitle';
import { icons } from '@/assets/icons';
import s from './Services.module.scss';

type PromoType = 'playlist' | 'individual';

interface ServicesProps {
  dict: Dictionary['promoInfo'];
}

export default function Services({ dict }: ServicesProps) {
  const promoInfo = dict;
  const { setModal } = useModal();
  const [activePromo, setActivePromo] = useState<PromoType>('playlist');
  const [isMobile, setIsMobile] = useState(false);
  const isScrollingRef = useRef(false);

  const stickyRef = useRef<HTMLDivElement | null>(null);
  const playlistSectionRef = useRef<HTMLDivElement | null>(null);
  const individualSectionRef = useRef<HTMLDivElement | null>(null);
  const contentWrapperRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const geometryCacheRef = useRef({
    stickyTop: 0,
    scrollStart: 0,
    scrollEnd: 0,
    scrollRange: 0,
    visibleHeight: 0,
    maxTranslate: 0,
    playlistTop: 0,
    playlistHeight: 0,
    individualTop: 0,
    individualHeight: 0,
    containerScrollHeight: 0,
    stickyOffsetHeight: 0,
    lastCacheUpdate: 0,
  });

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Intersection Observer for desktop scroll sync
  useEffect(() => {
    if (isMobile || typeof window === 'undefined') return;

    const playlistSection = playlistSectionRef.current;
    const individualSection = individualSectionRef.current;
    if (!playlistSection || !individualSection) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Don't update active state if we're in the middle of a programmatic scroll
        if (isScrollingRef.current) return;

        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            if (entry.target === playlistSection) {
              setActivePromo('playlist');
            } else if (entry.target === individualSection) {
              setActivePromo('individual');
            }
          }
        });
      },
      {
        root: null,
        threshold: [0, 0.5, 1],
        rootMargin: '-40% 0px -40% 0px',
      }
    );

    observer.observe(playlistSection);
    observer.observe(individualSection);

    return () => {
      observer.disconnect();
    };
  }, [isMobile]);

  // Update static geometry cache (values that don't change during scroll)
  const updateStaticGeometryCache = useCallback(() => {
    if (isMobile || typeof window === 'undefined') return;

    const stickyEl = stickyRef.current;
    const contentEl = contentWrapperRef.current;
    if (!stickyEl || !contentEl) return;

    const resolveStickyTop = () => {
      const computedTop = getComputedStyle(stickyEl).top;
      if (computedTop.endsWith('vh')) {
        const viewportHeight = window.innerHeight;
        return (parseFloat(computedTop) / 100) * viewportHeight;
      }
      return parseFloat(computedTop) || 0;
    };

    const containerEl = stickyEl.parentElement;
    if (!containerEl) return;

    const stickyTop = resolveStickyTop();

    // Batch all static layout reads together (these don't change during scroll)
    const containerScrollHeight = containerEl.scrollHeight;
    const stickyOffsetHeight = stickyEl.offsetHeight;

    const rightSectionContent = contentEl.parentElement;
    if (!rightSectionContent) return;

    const visibleHeight = rightSectionContent.offsetHeight;
    const maxTranslate = contentEl.scrollHeight - visibleHeight;

    const playlistSection = playlistSectionRef.current;
    const individualSection = individualSectionRef.current;

    const cache = geometryCacheRef.current;
    cache.stickyTop = stickyTop;
    cache.containerScrollHeight = containerScrollHeight;
    cache.stickyOffsetHeight = stickyOffsetHeight;
    cache.visibleHeight = visibleHeight;
    cache.maxTranslate = maxTranslate;

    if (playlistSection && individualSection) {
      cache.playlistTop = playlistSection.offsetTop;
      cache.playlistHeight = playlistSection.offsetHeight;
      cache.individualTop = individualSection.offsetTop;
      cache.individualHeight = individualSection.offsetHeight;
    }

    cache.lastCacheUpdate = Date.now();
  }, [isMobile]);

  // Update scroll-dependent values (only reads containerRect.top which changes on scroll)
  const updateScrollDependentValues = useCallback(() => {
    if (isMobile || typeof window === 'undefined') return;

    const stickyEl = stickyRef.current;
    if (!stickyEl) return;

    const containerEl = stickyEl.parentElement;
    if (!containerEl) return;

    const cache = geometryCacheRef.current;

    // Only read getBoundingClientRect when we need to update scroll-dependent values
    const containerRect = containerEl.getBoundingClientRect();
    const scrollStart = window.scrollY + containerRect.top - cache.stickyTop;
    const scrollEnd =
      scrollStart + cache.containerScrollHeight - cache.stickyOffsetHeight;
    const scrollRange = scrollEnd - scrollStart;

    cache.scrollStart = scrollStart;
    cache.scrollEnd = scrollEnd;
    cache.scrollRange = scrollRange;
  }, [isMobile]);

  // Scroll sync for desktop - simple transform based on scroll position
  const handleScroll = useCallback(() => {
    if (isMobile || typeof window === 'undefined') return;

    // Cancel any pending RAF
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    // Use requestAnimationFrame to batch layout reads and avoid forced reflows
    rafRef.current = requestAnimationFrame(() => {
      const stickyEl = stickyRef.current;
      const contentEl = contentWrapperRef.current;
      if (!stickyEl || !contentEl) return;

      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      if (prefersReducedMotion) {
        contentEl.style.transform = 'translate3d(0, 0, 0)';
        rafRef.current = null;
        return;
      }

      const cache = geometryCacheRef.current;
      const now = Date.now();

      // Update static cache less frequently - only if stale (older than 500ms) or on first run
      // This significantly reduces layout reads during rapid scrolling
      // Most layout changes happen on resize, not scroll
      if (now - cache.lastCacheUpdate > 500 || cache.lastCacheUpdate === 0) {
        updateStaticGeometryCache();
      }

      // Always update scroll-dependent values (they change on every scroll)
      // But this only reads getBoundingClientRect, which is necessary
      updateScrollDependentValues();

      const { scrollStart, scrollRange, maxTranslate, visibleHeight } =
        geometryCacheRef.current;

      if (scrollRange <= 0) {
        contentEl.style.transform = 'translate3d(0, 0, 0)';
        return;
      }

      const currentScroll = window.scrollY - scrollStart;
      const progress = Math.min(Math.max(currentScroll / scrollRange, 0), 1);

      if (maxTranslate > 0) {
        const translateY = -maxTranslate * progress;
        contentEl.style.transform = `translate3d(0, ${translateY}px, 0)`;

        // Update active state based on scroll progress (only if not in programmatic scroll)
        if (!isScrollingRef.current) {
          const playlistSection = playlistSectionRef.current;
          const individualSection = individualSectionRef.current;

          if (playlistSection && individualSection) {
            const {
              playlistTop,
              playlistHeight,
              individualTop,
              individualHeight,
            } = cache;

            // Account for the transform offset (translateY is negative)
            const adjustedPlaylistTop = playlistTop + translateY;
            const adjustedPlaylistBottom =
              playlistTop + playlistHeight + translateY;
            const adjustedIndividualTop = individualTop + translateY;
            const adjustedIndividualBottom =
              individualTop + individualHeight + translateY;

            // Check which section's center is closer to the visible area center
            const visibleCenter = visibleHeight / 2;
            const playlistCenter =
              (adjustedPlaylistTop + adjustedPlaylistBottom) / 2;
            const individualCenter =
              (adjustedIndividualTop + adjustedIndividualBottom) / 2;

            // Calculate distance from visible center for each section
            const playlistDistance = Math.abs(playlistCenter - visibleCenter);
            const individualDistance = Math.abs(
              individualCenter - visibleCenter
            );

            // Set active state based on which section is closer to center
            if (playlistDistance < individualDistance) {
              setActivePromo('playlist');
            } else {
              setActivePromo('individual');
            }
          }
        }
      } else {
        contentEl.style.transform = 'translate3d(0, 0, 0)';
      }

      rafRef.current = null;
    });
  }, [isMobile, updateStaticGeometryCache, updateScrollDependentValues]);

  const handleResize = useCallback(() => {
    // Force cache update on resize
    geometryCacheRef.current.lastCacheUpdate = 0;
    updateStaticGeometryCache();
    updateScrollDependentValues();
    handleScroll();
  }, [handleScroll, updateStaticGeometryCache, updateScrollDependentValues]);

  useEffect(() => {
    if (isMobile || typeof window === 'undefined') return;

    const stickyEl = stickyRef.current;
    const contentEl = contentWrapperRef.current;
    if (!stickyEl || !contentEl) return;

    // Initial cache update
    updateStaticGeometryCache();
    updateScrollDependentValues();
    handleScroll(); // Initial calculation

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [
    isMobile,
    handleScroll,
    handleResize,
    updateStaticGeometryCache,
    updateScrollDependentValues,
  ]);

  const renderContent = useCallback(
    (type: PromoType) => (
      <div className={s.content}>
        {type === 'playlist' ? (
          <>
            <p
              className={s.intro}
              dangerouslySetInnerHTML={{
                __html: promoInfo.description.stayVisible,
              }}
            />
            <p
              className={s.mainText}
              dangerouslySetInnerHTML={{
                __html: promoInfo.description.streams,
              }}
            />
            <div className={s.bottomHeadings}>
              <h3 className={s.heading}>{promoInfo.description.stayHeard}</h3>
            </div>
          </>
        ) : (
          <>
            <p
              className={s.intro}
              dangerouslySetInnerHTML={{ __html: promoInfo.description.intro }}
            />
            <p
              className={s.mainText}
              dangerouslySetInnerHTML={{ __html: promoInfo.description.main }}
            />
            <p
              className={s.processText}
              dangerouslySetInnerHTML={{
                __html: promoInfo.description.process,
              }}
            />
            <p
              className={s.resultText}
              dangerouslySetInnerHTML={{
                __html: promoInfo.description.result,
              }}
            />
            <div className={s.bottomHeadings}>
              <h3 className={s.heading}>{promoInfo.headings.impact}</h3>
              <h3 className={s.heading}>{promoInfo.headings.real}</h3>
            </div>
          </>
        )}
      </div>
    ),
    [promoInfo]
  );

  const closeModal = useCallback(() => {
    setModal(null);
  }, [setModal]);

  const openModal = useCallback(
    (type: PromoType) => {
      const href = type === 'playlist' ? '#plans-prices' : '#individual-promo';

      setModal(
        <div className={s.modal} onClick={(e) => e.stopPropagation()}>
          <button
            className={s.modalCloseButton}
            onClick={closeModal}
            aria-label="Close"
          >
            ×
          </button>
          <div className={s.modalContent}>
            {renderContent(type)}
            <CTAButton
              mode="link"
              href={href}
              className={s.modalCta}
              onClick={closeModal}
            >
              {promoInfo.button}
            </CTAButton>
          </div>
        </div>
      );
    },
    [setModal, closeModal, renderContent, promoInfo.button]
  );

  const handlePromoClick = useCallback(
    (type: PromoType) => {
      if (isMobile) {
        setActivePromo(type);
        openModal(type);
      } else {
        setActivePromo(type);
        // Scroll to the target section on desktop
        const targetSection =
          type === 'playlist'
            ? playlistSectionRef.current
            : individualSectionRef.current;

        if (targetSection) {
          const stickyEl = stickyRef.current;
          const contentEl = contentWrapperRef.current;
          if (!stickyEl || !contentEl) return;

          const containerEl = stickyEl.parentElement;
          if (!containerEl) return;

          // Update cache before calculating scroll position
          updateStaticGeometryCache();
          updateScrollDependentValues();
          const cache = geometryCacheRef.current;

          if (cache.scrollRange <= 0) return;

          // Calculate target section position within content wrapper
          const targetOffsetTop = targetSection.offsetTop;
          const { maxTranslate } = cache;

          if (maxTranslate <= 0) return;

          // Calculate progress (0 to 1) for the target section
          const targetProgress = Math.min(
            Math.max(targetOffsetTop / maxTranslate, 0),
            1
          );

          // Convert progress to scroll position
          const targetScroll =
            cache.scrollStart + cache.scrollRange * targetProgress;

          // Set flag to prevent scroll handler from overriding active state during smooth scroll
          isScrollingRef.current = true;

          window.scrollTo({ top: targetScroll, behavior: 'smooth' });

          // Clear flag after scroll animation completes (smooth scroll typically takes ~500ms)
          setTimeout(() => {
            isScrollingRef.current = false;
          }, 600);
        }
      }
    },
    [
      isMobile,
      setActivePromo,
      openModal,
      updateStaticGeometryCache,
      updateScrollDependentValues,
    ]
  );

  const onActivate = useCallback(
    (e: React.KeyboardEvent, action: () => void) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        action();
      }
    },
    []
  );

  return (
    <Section id="services" className={s.promoInfo} padding="xl">
      <Container className={s.container}>
        <div ref={stickyRef} className={s.stickySection}>
          {/* Left Section - Title */}
          <div className={s.leftSection}>
            <BrandTitle
              firstLine={promoInfo.title}
              secondLine={promoInfo.subtitle}
              secondLineColor="white"
            />
          </div>

          {/* Middle Section - Music Player */}
          <div className={s.middleSection}>
            <div className={s.musicPlayer}>
              <div className={s.screen}>
                <div className={s.screenInnerBorder}>
                  <div
                    className={`${s.screenTitle} ${activePromo === 'playlist' ? s.active : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handlePromoClick('playlist')}
                    onKeyDown={(e) =>
                      onActivate(e, () => handlePromoClick('playlist'))
                    }
                    dangerouslySetInnerHTML={{
                      __html: promoInfo.musicPlayer.playlistPromo,
                    }}
                  />
                  <div
                    className={`${s.screenTitle} ${activePromo === 'individual' ? s.active : ''}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handlePromoClick('individual')}
                    onKeyDown={(e) =>
                      onActivate(e, () => handlePromoClick('individual'))
                    }
                    dangerouslySetInnerHTML={{
                      __html: promoInfo.musicPlayer.individualPromo,
                    }}
                  />
                </div>
              </div>
              <div className={s.controlWheel}>
                <div className={s.wheel}>
                  <div className={s.brandLogo}>{promoInfo.title}</div>
                  <div
                    className={`${s.controlButton} ${activePromo === 'individual' ? s.active : ''}`}
                    data-position="left"
                    role="button"
                    tabIndex={0}
                    aria-label="Go to playlist section"
                    onClick={() => handlePromoClick('playlist')}
                    onKeyDown={(e) =>
                      onActivate(e, () => handlePromoClick('playlist'))
                    }
                  >
                    <svg className={s.controlIcon}>
                      <use xlinkHref={`${icons.src}#icon-ipod-nav`} />
                    </svg>
                  </div>
                  <div className={s.wheelInner}></div>
                  <div
                    className={`${s.controlButton} ${activePromo === 'playlist' ? s.active : ''}`}
                    data-position="right"
                    role="button"
                    tabIndex={0}
                    aria-label="Go to individual section"
                    onClick={() => handlePromoClick('individual')}
                    onKeyDown={(e) =>
                      onActivate(e, () => handlePromoClick('individual'))
                    }
                  >
                    <svg
                      className={s.controlIcon}
                      style={{ transform: 'rotate(180deg)' }}
                    >
                      <use xlinkHref={`${icons.src}#icon-ipod-nav`} />
                    </svg>
                  </div>
                  <div className={s.controlButton} data-position="bottom">
                    <svg className={s.controlIcon}>
                      <use xlinkHref={`${icons.src}#icon-ipod-playpause`} />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Promotional Content (Desktop only) */}
          {!isMobile && (
            <div className={s.rightSection}>
              <div className={s.rightSectionContent}>
                <div ref={contentWrapperRef} className={s.contentWrapper}>
                  <div className={s.textSection} ref={playlistSectionRef}>
                    {renderContent('playlist')}
                  </div>
                  <div className={s.textSection} ref={individualSectionRef}>
                    {renderContent('individual')}
                  </div>
                </div>
              </div>
              <CTAButton
                mode="link"
                href="#plans-prices"
                className={s.ctaButton}
              >
                {promoInfo.button}
              </CTAButton>
            </div>
          )}
        </div>
      </Container>
    </Section>
  );
}
